import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'swiftai-theme';

function getStoredSetting() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {}
  return null;
}

function resolveTheme(setting) {
  if (setting === 'light' || setting === 'dark') return setting;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'dark';
  }
}

function applyTheme(resolved) {
  document.documentElement.setAttribute('data-theme', resolved);
}

export function ThemeProvider({ children }) {
  const [setting, setSetting] = useState(() => getStoredSetting() || 'dark');
  const [resolved, setResolved] = useState(() => resolveTheme(getStoredSetting() || 'dark'));

  const setTheme = useCallback((newSetting) => {
    const newResolved = resolveTheme(newSetting);
    setSetting(newSetting);
    setResolved(newResolved);
    applyTheme(newResolved);
    try { localStorage.setItem(STORAGE_KEY, newSetting); } catch {}
  }, []);

  // Appliquer au montage (l'anti-flash script dans index.html a déjà posé data-theme,
  // on s'assure juste que l'état React est cohérent)
  useEffect(() => {
    applyTheme(resolved);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suivre les changements OS quand le mode "system" est actif
  useEffect(() => {
    if (setting !== 'system') return;
    try {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e) => {
        const r = e.matches ? 'dark' : 'light';
        setResolved(r);
        applyTheme(r);
      };
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } catch {}
  }, [setting]);

  return (
    <ThemeContext.Provider value={{ theme: setting, resolvedTheme: resolved, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme doit être utilisé dans un ThemeProvider');
  return ctx;
}
